/* assets/dv-sidecart.js
   Sidecart AJAX + Modal options (couleur) centrée:
   - Intercepte tous les submit /cart/add en CAPTURE
   - Si variantes et pas de sélecteurs => ouvre une MODAL centrée pour choisir (couleur/options)
   - Au clic sur une couleur (si c’est la seule option requise) => ajout direct
   - Ensuite: ouvre sidecart + refresh /cart.js
   + Suppression item (cart/change.js)
   + Vider panier (cart/clear.js)
   + Compteur header fiable
*/
(() => {
  const SIDE_ID = 'dv-sidecart';
  const OPEN_CLASS = 'is-open';

  const selectors = {
    sidecart: `#${SIDE_ID}`,
    content: `#${SIDE_ID} [data-dv-content]`,
    foot: `#${SIDE_ID} [data-dv-foot]`,
    close: `#${SIDE_ID} [data-dv-close]`,
  };

  // ---------- utils ----------
  const $ = (sel, root = document) => root.querySelector(sel);

  function getSidecart() {
    const side = $(selectors.sidecart);
    if (!side) return null;
    return {
      side,
      content: $(selectors.content, side),
      foot: $(selectors.foot, side),
    };
  }

  function cartAddUrl() {
    const base = window?.routes?.cart_add_url || '/cart/add';
    return base.endsWith('.js') ? base : `${base}.js`;
  }
  function cartJsUrl() {
    return '/cart.js';
  }
  function cartChangeUrl() {
    const base = window?.routes?.cart_change_url || '/cart/change';
    return base.endsWith('.js') ? base : `${base}.js`;
  }
  function cartClearUrl() {
    return '/cart/clear.js';
  }
  function cartPageUrl() {
    return window?.routes?.cart_url || '/cart';
  }

  function safeEscape(str) {
    return String(str ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function formatMoney(cents) {
    const v = Number(cents || 0);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const fmt = window.Shopify.money_format || '{{amount}}';
      try {
        return window.Shopify.formatMoney(v, fmt);
      } catch (e) {}
    }
    const amount = (v / 100).toFixed(2).replace('.', ',');
    return `${amount} €`;
  }

  function lockScroll(lock) {
    document.documentElement.style.overflow = lock ? 'hidden' : '';
  }

  // ---------- open/close sidecart ----------
  let lastFocused = null;

  function openSidecart() {
    const sc = getSidecart();
    if (!sc) return;

    lastFocused = document.activeElement;

    sc.side.classList.add(OPEN_CLASS);
    sc.side.setAttribute('aria-hidden', 'false');
    document.body.classList.add('dv-sidecart-open');
    document.body.style.overflow = 'hidden';

    try {
      if (typeof window.trapFocus === 'function') {
        window.trapFocus(sc.side, sc.side.querySelector('button, a, [tabindex]:not([tabindex^="-"])') || sc.side);
      }
    } catch (e) {}
  }

  function closeSidecart() {
    const sc = getSidecart();
    if (!sc) return;

    sc.side.classList.remove(OPEN_CLASS);
    sc.side.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('dv-sidecart-open');
    document.body.style.overflow = '';

    try {
      if (typeof window.removeTrapFocus === 'function') {
        window.removeTrapFocus(lastFocused || null);
      }
    } catch (e) {}

    if (lastFocused && typeof lastFocused.focus === 'function') {
      try { lastFocused.focus(); } catch (e) {}
    }
    lastFocused = null;
  }

  function bindCloseHandlers() {
    document.querySelectorAll(selectors.close).forEach((el) => {
      if (el.__dvBound) return;
      el.__dvBound = true;
      el.addEventListener('click', (e) => {
        e.preventDefault();
        closeSidecart();
      });
    });

    if (!document.__dvEscBound) {
      document.__dvEscBound = true;
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const sc = getSidecart();
          if (sc?.side?.classList?.contains(OPEN_CLASS)) closeSidecart();
          if (OptModal.isOpen()) OptModal.close();
        }
      });
    }
  }

  function setLoading(isLoading) {
    const sc = getSidecart();
    if (!sc) return;
    if (isLoading) {
      sc.content.innerHTML = `<div style="padding:10px 0; font-weight:800; color: rgba(11,18,32,.70);">Chargement…</div>`;
      sc.foot.innerHTML = '';
    }
  }

  function setError(message) {
    const sc = getSidecart();
    if (!sc) return;

    sc.content.innerHTML = `
      <div style="padding:10px 0;">
        <div style="font-weight:900; margin-bottom:6px;">Oups</div>
        <div style="color: rgba(11,18,32,.75); font-weight:700;">${safeEscape(message || "Impossible d’effectuer l’action.")}</div>
      </div>
    `;
    sc.foot.innerHTML = `
      <div class="dv-row2">
        <a class="dv-btn dv-btn--primary" href="${cartPageUrl()}">Voir le panier</a>
        <button class="dv-btn dv-btn--ghost" type="button" data-dv-close>Fermer</button>
      </div>
    `;
    bindCloseHandlers();
  }

  // ---------- cart API ----------
  async function fetchCart() {
    const res = await fetch(cartJsUrl(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`cart.js error (${res.status})`);
    return await res.json();
  }

  async function changeLine(line, quantity) {
    const res = await fetch(cartChangeUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = data?.description || data?.message || `Erreur panier (${res.status})`;
      throw new Error(msg);
    }
    return data;
  }

  async function clearCart() {
    const res = await fetch(cartClearUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({}),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = data?.description || data?.message || `Erreur panier (${res.status})`;
      throw new Error(msg);
    }
    return data;
  }

  function updateHeaderCartCount(cart) {
    const count = Number(cart?.item_count ?? 0);

    document.querySelectorAll('[data-cart-count], .cart-count').forEach((el) => {
      if (!el) return;
      el.textContent = String(count);
    });

    const cartIcon = document.getElementById('cart-icon-bubble');
    if (!cartIcon) return;

    let bubble = cartIcon.querySelector('.cart-count-bubble');

    if (count > 0) {
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        bubble.innerHTML = `
          <span aria-hidden="true"></span>
          <span class="visually-hidden"></span>
        `;
        cartIcon.appendChild(bubble);
      }
      const ariaHidden = bubble.querySelector('span[aria-hidden="true"]');
      const srOnly = bubble.querySelector('.visually-hidden');

      if (ariaHidden) ariaHidden.textContent = String(count);
      if (srOnly) srOnly.textContent = `Panier : ${count} article${count > 1 ? 's' : ''}`;
      bubble.style.display = '';
    } else {
      if (bubble) bubble.remove();
    }
  }

  function renderCart(cart) {
    const sc = getSidecart();
    if (!sc) return;

    const items = Array.isArray(cart?.items) ? cart.items : [];

    if (!items.length) {
      sc.content.innerHTML = `
        <div style="padding:10px 0; font-weight:800; color: rgba(11,18,32,.70);">
          Votre panier est vide.
        </div>
      `;
      sc.foot.innerHTML = `
        <div class="dv-row2">
          <a class="dv-btn dv-btn--primary" href="/collections/all">Continuer mes achats</a>
          <button class="dv-btn dv-btn--ghost" type="button" data-dv-close>Fermer</button>
        </div>
      `;
      bindCloseHandlers();
      updateHeaderCartCount(cart);
      return;
    }

    const itemsHtml = items
      .map((item, idx) => {
        const line = idx + 1;
        const title = safeEscape(item.product_title || item.title || 'Produit');
        const url = safeEscape(item.url || '#');

        const img =
          item.image ||
          item.featured_image?.url ||
          item.featured_image?.src ||
          item.featured_image ||
          '';
        const imgHtml = img ? `<img src="${safeEscape(img)}" alt="${title}" loading="lazy">` : '';

        const variantText = Array.isArray(item.options_with_values)
          ? item.options_with_values.map((o) => `${safeEscape(o.name)}: ${safeEscape(o.value)}`).join(' • ')
          : (item.variant_title && item.variant_title !== 'Default Title' ? safeEscape(item.variant_title) : '');

        const qty = Number(item.quantity || 1);
        const linePrice = Number(item.final_line_price ?? item.line_price ?? 0);

        return `
          <article class="dv-item">
            <a class="dv-item__img" href="${url}" aria-label="${title}">
              ${imgHtml}
            </a>
            <div>
              <p class="dv-item__title">
                <a href="${url}" style="color:inherit; text-decoration:none;">${title}</a>
              </p>
              ${variantText ? `<div class="dv-item__meta">${variantText}</div>` : ''}
              <div class="dv-item__meta" style="justify-content:space-between; align-items:center;">
                <span>Qté: ${qty}</span>
                <button
                  type="button"
                  data-dv-remove-line="${line}"
                  style="
                    appearance:none;border:0;background:transparent;
                    font-weight:900;font-size:12px;cursor:pointer;
                    color: rgba(220,38,38,.90);
                    padding:4px 6px;border-radius:8px;
                  "
                  aria-label="Supprimer ${title} du panier"
                >Supprimer</button>
              </div>
              <div class="dv-item__price">${formatMoney(linePrice)}</div>
            </div>
          </article>
        `;
      })
      .join('');

    sc.content.innerHTML = `<div class="dv-cartitems">${itemsHtml}</div>`;

    const total = Number(cart.total_price || 0);

    sc.foot.innerHTML = `
      <div class="dv-subtotal">
        <span>Total</span>
        <span>${formatMoney(total)}</span>
      </div>
      <div class="dv-row2">
        <a class="dv-btn dv-btn--primary" href="/checkout">Procéder au paiement</a>
        <a class="dv-btn dv-btn--ghost" href="${cartPageUrl()}">Voir le panier</a>
      </div>
      <div style="margin-top:10px;">
        <button
          type="button"
          data-dv-clear
          class="dv-btn dv-btn--ghost"
          style="border-color: rgba(220,38,38,.25); color: rgba(220,38,38,.90);"
        >Vider le panier</button>
      </div>
    `;

    bindCloseHandlers();
    updateHeaderCartCount(cart);
  }

  async function refreshAndRender() {
    const cart = await fetchCart();
    renderCart(cart);
    return cart;
  }

  // ---------- MODAL (options) ----------
  const OptModal = (() => {
    const ID = 'dv-optmodal';
    let mounted = false;

    function ensure() {
      if (mounted) return;
      mounted = true;

      const el = document.createElement('div');
      el.id = ID;
      el.className = 'dv-optmodal';
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML = `
        <div class="dv-optmodal__backdrop" data-dv-opt-close></div>
        <div class="dv-optmodal__panel" role="dialog" aria-modal="true" aria-label="Choix des options">
          <div class="dv-optmodal__head">
            <div class="dv-optmodal__title">Choisir une option</div>
            <button class="dv-optmodal__close" type="button" data-dv-opt-close aria-label="Fermer">×</button>
          </div>
          <div class="dv-optmodal__body" data-dv-opt-body></div>
        </div>
      `;
      document.body.appendChild(el);

      el.addEventListener('click', (e) => {
        if (e.target && e.target.closest('[data-dv-opt-close]')) {
          e.preventDefault();
          close();
        }
      });
    }

    function open() {
      ensure();
      const el = document.getElementById(ID);
      if (!el) return;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      lockScroll(true);
    }

    function close() {
      const el = document.getElementById(ID);
      if (!el) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      lockScroll(false);
    }

    function isOpen() {
      const el = document.getElementById(ID);
      return !!el && el.classList.contains('is-open');
    }

    function bodyEl() {
      const el = document.getElementById(ID);
      if (!el) return null;
      return el.querySelector('[data-dv-opt-body]');
    }

    function setTitle(text) {
      const el = document.getElementById(ID);
      if (!el) return;
      const t = el.querySelector('.dv-optmodal__title');
      if (t) t.textContent = text || 'Choisir une option';
    }

    return { open, close, isOpen, bodyEl, setTitle };
  })();

  // ---------- Variant / options gate ----------
  const state = {
    product: null,
    selected: {},
    requiredOptionIndexes: [],
    pendingPayload: null, // { entries: Array<[k,v]> }
  };

  const normalizeName = (s) => String(s ?? '').trim().toLowerCase();

  function findColorOptionIndex(product) {
    const names = (product.options || []).map((x) => normalizeName(x?.name ?? x));
    let idx = names.indexOf('couleur');
    if (idx !== -1) return idx;
    idx = names.indexOf('color');
    if (idx !== -1) return idx;
    return null;
  }

  function getOptionName(product, optIndex) {
    const raw = product.options?.[optIndex];
    if (typeof raw === 'string') return raw;
    if (raw && typeof raw === 'object' && raw.name) return String(raw.name);
    return `Option ${optIndex + 1}`;
  }

  function getOptionValues(product, optIndex) {
    const key = `option${optIndex + 1}`;
    const seen = new Set();
    const out = [];
    (product.variants || []).forEach((v) => {
      const val = v?.[key];
      if (!val) return;
      if (!seen.has(val)) {
        seen.add(val);
        out.push(val);
      }
    });
    return out;
  }

  function variantMatchesSelection(variant, selected) {
    const opts = [variant.option1, variant.option2, variant.option3];
    for (const [idxStr, val] of Object.entries(selected)) {
      const idx = Number(idxStr);
      if (!val) return false;
      if ((opts[idx] || '') !== val) return false;
    }
    return true;
  }

  function getPossibleVariant(product, selected) {
    return (product.variants || []).find((v) => variantMatchesSelection(v, selected)) || null;
  }

  function isSelectionComplete() {
    return state.requiredOptionIndexes.every((i) => !!state.selected[i]);
  }

  function shouldDisableValue(product, optIndex, value) {
    const testSel = { ...state.selected, [optIndex]: value };
    return !(product.variants || []).some((v) => {
      if (!v.available) return false;
      const opts = [v.option1, v.option2, v.option3];
      for (const [idxStr, val] of Object.entries(testSel)) {
        const idx = Number(idxStr);
        if (!val) continue;
        if ((opts[idx] || '') !== val) return false;
      }
      return true;
    });
  }

  async function fetchProductByHandle(handle) {
    const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Produit introuvable.');
    return await res.json();
  }

  function hasVariantSelectorsInForm(form) {
    if (form.querySelector('variant-selects')) return true;
    if (form.querySelector('[name^="options["], select[name^="options["], input[name^="options["]')) return true;
    if (form.querySelector('fieldset')) return true;
    return false;
  }

  function getHandleFromContext(form) {
    const h1 = form.getAttribute('data-product-handle');
    if (h1) return h1;

    const any = form.querySelector('[data-product-handle]');
    if (any?.getAttribute('data-product-handle')) return any.getAttribute('data-product-handle');

    try {
      const u = new URL(window.location.href);
      const m = u.pathname.match(/\/products\/([^\/\?]+)/);
      if (m) return m[1];
    } catch {}

    return null;
  }

  function formDataEntries(fd) {
    return Array.from(fd.entries()).map(([k, v]) => [k, v]);
  }

  function buildFormDataFromEntries(entries) {
    const fd = new FormData();
    entries.forEach(([k, v]) => fd.append(k, v));
    return fd;
  }

  async function postAddWithVariant(variantId) {
    const entries = (state.pendingPayload?.entries || []).filter(([k]) => k !== 'id');
    entries.push(['id', String(variantId)]);
    const fd = buildFormDataFromEntries(entries);

    const res = await fetch(cartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: fd,
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.description || data?.message || "Erreur lors de l’ajout au panier.");
    }
    return data;
  }

  function renderOptionModal(product) {
    OptModal.setTitle('Choisissez votre couleur');

    const colorIdx = findColorOptionIndex(product);

    const required = state.requiredOptionIndexes.slice().sort((a, b) => {
      if (colorIdx === null) return a - b;
      if (a === colorIdx) return -1;
      if (b === colorIdx) return 1;
      return a - b;
    });

    // ✅ si une seule option requise (souvent couleur) => ajout direct au clic
    const autoAddOnClick = required.length === 1;

    let html = '';
    required.forEach((optIndex) => {
      const optName = getOptionName(product, optIndex);
      const values = getOptionValues(product, optIndex);

      const isColor = ['couleur', 'color'].includes(normalizeName(optName));

      const swatches = values.map((val) => {
  const selected = state.selected[optIndex] === val;
  const disabled = shouldDisableValue(product, optIndex, val);

  const img = isColor ? getImageForOptionValue(product, optIndex, val) : '';

  // ✅ si couleur -> carte avec image
  if (isColor) {
    return `
      <button
        class="dv-swatch dv-swatch--card ${selected ? 'is-selected' : ''}"
        type="button"
        data-dv-opt-index="${optIndex}"
        data-dv-opt-val="${encodeURIComponent(val)}"
        ${disabled ? 'disabled' : ''}
        aria-pressed="${selected ? 'true' : 'false'}"
      >
        <div class="dv-swatch__imgwrap">
          ${img ? `<img src="${safeEscape(img)}" alt="${safeEscape(val)}" loading="lazy">` : ''}
        </div>
        <div class="dv-swatch__label">
          <span>${safeEscape(val)}</span>
        </div>
      </button>
    `;
  }

  // sinon -> petit bouton simple (autres options)
  return `
    <button
      class="dv-swatch ${selected ? 'is-selected' : ''}"
      type="button"
      data-dv-opt-index="${optIndex}"
      data-dv-opt-val="${encodeURIComponent(val)}"
      ${disabled ? 'disabled' : ''}
      aria-pressed="${selected ? 'true' : 'false'}"
    >${safeEscape(val)}</button>
  `;
}).join('');





function imgSized(url, w = 220) {
  if (!url) return '';
  try {
    const u = new URL(url, window.location.origin);
    // Shopify CDN accepte ?width=
    if (!u.searchParams.get('width')) u.searchParams.set('width', String(w));
    return u.toString();
  } catch {
    // fallback si URL relative/strange
    return url.includes('?') ? `${url}&width=${w}` : `${url}?width=${w}`;
  }
}

function getImageForOptionValue(product, optIndex, value) {
  // 1) prend la 1ère variante qui matche cette valeur et qui a une image
  const key = `option${optIndex + 1}`;
  const v = (product.variants || []).find(x => (x?.[key] === value) && (x?.featured_image || x?.image));
  const raw =
    v?.featured_image?.src ||
    v?.featured_image ||
    v?.image ||
    null;

  // 2) fallback: image produit
  const fallback =
    product?.featured_image ||
    (Array.isArray(product?.images) ? product.images[0] : null) ||
    null;

  return imgSized(raw || fallback, 220);
}



      html += `
        <div class="dv-optblock">
          <div class="dv-optblock__top">
            <div class="dv-optblock__label">${isColor ? 'Choisissez votre couleur' : safeEscape(optName)}</div>
            <div class="dv-optblock__req">Obligatoire</div>
          </div>
          <div class="dv-swatches">${swatches}</div>
          ${!state.selected[optIndex] ? `<div class="dv-optmodal__hint">Sélection obligatoire pour continuer</div>` : ``}
        </div>
      `;
    });

    const body = OptModal.bodyEl();
    if (!body) return;
    body.innerHTML = html;

    // listeners
    body.querySelectorAll('[data-dv-opt-index][data-dv-opt-val]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const idx = Number(btn.getAttribute('data-dv-opt-index'));
        const val = decodeURIComponent(btn.getAttribute('data-dv-opt-val'));
        state.selected[idx] = val;

        // refresh UI selection
        renderOptionModal(product);

        // ✅ si auto-add (ex: seulement couleur)
        if (autoAddOnClick && isSelectionComplete()) {
          try {
            // lock UI
            OptModal.setTitle('Ajout au panier…');

            const variant = getPossibleVariant(product, state.selected);
            if (!variant || !variant.available) throw new Error('Variante indisponible.');

            await postAddWithVariant(variant.id);

            // ferme modal -> ouvre sidecart -> affiche panier
            OptModal.close();
            openSidecart();
            setLoading(true);
            await refreshAndRender();
          } catch (err) {
            // si erreur => on ouvre quand même le sidecart avec l’erreur (plus clair)
            OptModal.close();
            openSidecart();
            setError(err?.message || String(err));
          }
        }
      });
    });
  }

  async function openGateForForm(form) {
    const handle = getHandleFromContext(form);
    if (!handle) {
      openSidecart();
      setLoading(true);
      await refreshAndRender();
      return;
    }

    const fd = new FormData(form);
    state.pendingPayload = { entries: formDataEntries(fd) };

    const product = await fetchProductByHandle(handle);
    state.product = product;
    state.selected = {};

    const optionCount = (product.options || []).length;
    const hasVariants = (product.variants || []).length > 1;

    // options requises: couleur prioritaire + autres si multi-variantes
    state.requiredOptionIndexes = [];
    const colorIdx = findColorOptionIndex(product);
    if (colorIdx !== null) state.requiredOptionIndexes.push(colorIdx);

    if (hasVariants && optionCount > 0) {
      for (let i = 0; i < optionCount; i++) {
        if (!state.requiredOptionIndexes.includes(i)) state.requiredOptionIndexes.push(i);
      }
    }

    // ✅ Si aucune option => ajout direct du 1er variant dispo + sidecart
    if (!state.requiredOptionIndexes.length) {
      const firstAvail = (product.variants || []).find((v) => v.available) || product.variants?.[0];
      if (!firstAvail) {
        openSidecart();
        setLoading(true);
        await refreshAndRender();
        return;
      }

      await postAddWithVariant(firstAvail.id);

      openSidecart();
      setLoading(true);
      await refreshAndRender();
      return;
    }

    // ✅ Sinon : ouvre MODAL centrée
    OptModal.open();
    renderOptionModal(product);
  }

  // ---------- AJAX add to cart ----------
  function getSubmitButton(form) {
    return form.querySelector('button[type="submit"]') || form.querySelector('input[type="submit"]') || null;
  }

  function disableSubmit(form, disabled) {
    const btn = getSubmitButton(form);
    if (!btn) return;
    btn.disabled = !!disabled;
    if (disabled) btn.setAttribute('aria-disabled', 'true');
    else btn.removeAttribute('aria-disabled');
  }

  async function ajaxAddToCart(form) {
    const fd = new FormData(form);

    if (!fd.get('id')) {
      const hiddenId = form.querySelector('input[name="id"]');
      if (hiddenId?.value) fd.set('id', hiddenId.value);
    }

    const res = await fetch(cartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: fd,
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = data?.description || data?.message || data?.status || "Erreur lors de l’ajout au panier.";
      throw new Error(msg);
    }
    return data;
  }

  function bindAddToCartInterceptor() {
    if (document.__dvAddBound) return;
    document.__dvAddBound = true;

    document.addEventListener('submit', async (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      const action = (form.getAttribute('action') || '').toLowerCase();
      const isAdd =
        action.includes('/cart/add') ||
        form.matches('form[action*="/cart/add"]') ||
        form.matches('form[action*="/cart/add.js"]');

      if (!isAdd) return;

      e.preventDefault();
      e.stopPropagation();

      disableSubmit(form, true);

      try {
        const hasSelectors = hasVariantSelectorsInForm(form);

        // ✅ si pas de sélecteurs -> modal options
        if (!hasSelectors) {
          await openGateForForm(form);
        } else {
          // ajout normal
          await ajaxAddToCart(form);
          openSidecart();
          setLoading(true);
          await refreshAndRender();
        }
      } catch (err) {
        openSidecart();
        setError(err?.message || String(err));
      } finally {
        disableSubmit(form, false);
      }
    }, true);
  }

  // ---------- remove / clear ----------
  function bindCartActions() {
    if (document.__dvCartActionsBound) return;
    document.__dvCartActionsBound = true;

    document.addEventListener('click', async (e) => {
      const removeBtn = e.target.closest('[data-dv-remove-line]');
      const clearBtn = e.target.closest('[data-dv-clear]');
      if (!removeBtn && !clearBtn) return;

      const sc = getSidecart();
      if (!sc) return;

      e.preventDefault();
      e.stopPropagation();

      openSidecart();
      setLoading(true);

      try {
        if (removeBtn) {
          const line = Number(removeBtn.getAttribute('data-dv-remove-line'));
          if (!Number.isFinite(line) || line <= 0) throw new Error('Ligne invalide.');
          await changeLine(line, 0);
          await refreshAndRender();
        } else if (clearBtn) {
          await clearCart();
          await refreshAndRender();
        }
      } catch (err) {
        setError(err?.message || String(err));
      }
    }, true);
  }

  // ---------- init ----------
  function init() {
    bindCloseHandlers();
    bindAddToCartInterceptor();
    bindCartActions();

    window.DVSidecart = {
      open: () => {
        openSidecart();
        refreshAndRender().catch(() => {});
      },
      close: () => closeSidecart(),
      refresh: () => refreshAndRender(),
    };

    document.addEventListener('click', (e) => {
      const t = e.target.closest('[data-dv-open-cart]');
      if (!t) return;
      e.preventDefault();
      openSidecart();
      setLoading(true);
      refreshAndRender().catch((err) => setError(err?.message || String(err)));
    });

    // compteur au chargement (utile si panier déjà rempli)
    fetchCart().then(updateHeaderCartCount).catch(() => {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
